import { useState } from "react";
import { Card, CardHeader, CardBody } from "../../components/ui/Card";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import { geometryApi } from "../../api/blades";
import useProjectStore from "../../store/projectStore";
import useSaveShortcut from "../../hooks/useSaveShortcut";
import useBladeReadonly from "../../hooks/useBladeReadonly";

const FIELD_DEFS = [
  { key: "rpm",       label: "Speed",        unit: "rpm",   type: "number" },
  { key: "flow_rate", label: "Flow rate Q",   unit: "m³/s", type: "number", step: "0.001" },
  { key: "head",      label: "Total head H",  unit: "m",    type: "number" },
  { key: "eta_vol",   label: "η_vol",         unit: "–",    type: "number", step: "0.01" },
  { key: "n_blades",  label: "No. of blades", unit: "–",    type: "number" },
];

const RESULT_DEFS = [
  { key: "nq",           label: "Specific speed nq",  unit: "" },
  { key: "psi2",         label: "Head coeff. ψ₂",     unit: "" },
  { key: "p_hyd",        label: "P_hyd",              unit: "kW" },
  { key: "d2",           label: "D₂",                 unit: "m" },
  { key: "b2",           label: "b₂",                 unit: "m" },
  { key: "d1",           label: "D₁",                 unit: "m" },
  { key: "beta2",        label: "β₂",                 unit: "°" },
  { key: "NPSH3_medium", label: "NPSH₃% medium",      unit: "m" },
];

export default function InitPage() {
  const { activeDesign, updateDesign } = useProjectStore();
  const { isReadOnly } = useBladeReadonly();
  const [form, setForm] = useState({
    rpm: activeDesign?.rpm || "",
    flow_rate: activeDesign?.flow_rate || "",
    head: activeDesign?.head || "",
    eta_vol: activeDesign?.eta_vol || 0.97,
    n_blades: activeDesign?.n_blades || 6,
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const compute = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await geometryApi.impellerInit({
        rpm: Number(form.rpm),
        flow_rate: Number(form.flow_rate),
        head: Number(form.head),
        eta_vol: Number(form.eta_vol),
        n_blades: Number(form.n_blades),
      });
      setResults(data);
    } catch (e) {
      setError(e.response?.data?.error || "Computation failed");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!results || !activeDesign) return;
    setSaving(true);
    try {
      await updateDesign(activeDesign.id, results);
    } catch { /* 403 or network error — stay silent, UI already blocked */ }
    finally {
      setSaving(false);
    }
  };
  useSaveShortcut(save, { disabled: saving || isReadOnly });

  return (
    <div className="p-6 grid grid-cols-2 gap-6">
      {/* Input */}
      <Card>
        <CardHeader title="Design Point" subtitle="Hydraulic operating conditions" />
        <CardBody className="space-y-4">
          {FIELD_DEFS.map(({ key, label, unit, ...rest }) => (
            <Input key={key} label={label} unit={unit}
                   value={form[key]} onChange={set(key)} {...rest} />
          ))}
          {error && <p className="text-sm text-red-500">{error}</p>}
          <Button onClick={compute} loading={loading} className="w-full justify-center mt-2">
            Compute
          </Button>
        </CardBody>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader title="Sizing Results" subtitle="Computed geometry parameters"
                    action={results && !isReadOnly && (
                      <Button variant="outline" size="sm" onClick={save} loading={saving} shortcut="Ctrl+S">
                        Save to design
                      </Button>
                    )} />
        <CardBody>
          {!results ? (
            <p className="text-gray-400 text-sm">Run computation to see results.</p>
          ) : (
            <dl className="divide-y divide-gray-100">
              {RESULT_DEFS.map(({ key, label, unit }) => (
                results[key] != null && (
                  <div key={key} className="flex justify-between py-2.5">
                    <dt className="text-sm text-gray-500">{label}</dt>
                    <dd className="text-sm font-semibold text-gray-900">
                      {typeof results[key] === "number"
                        ? results[key].toFixed(4)
                        : results[key]}
                      {unit && <span className="ml-1 text-gray-400 font-normal">{unit}</span>}
                    </dd>
                  </div>
                )
              ))}
            </dl>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
